// Shared form primitives (§18.1/§18.5) — one implementation of the field wrapper, input/
// select/button styling, and the amount input (inline arithmetic + shorthand preview, EX-4/
// AI-6), reused by every form from WP-1.1 onward. Extracted out of AuthCard once a second
// consumer (AccountForm) needed the same pieces.
import { type CurrencyCode, parseAmountInput } from '@pfos/engine';

export function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string | undefined;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <label className="mb-3 block" htmlFor={htmlFor}>
      <span className="mb-1 block text-sm text-muted">{label}</span>
      {children}
      {error !== undefined && <span className="mt-1 block text-sm text-danger">{error}</span>}
    </label>
  );
}

export const inputClass =
  'w-full rounded-[var(--radius-control)] border border-border bg-bg px-3 py-2 text-ink outline-none ' +
  'focus:border-primary focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--primary)_18%,transparent)]';

export const selectClass = inputClass;

// Full-width — single-column layouts (AuthCard).
export const primaryButtonClass =
  'w-full rounded-[var(--radius-control)] bg-primary px-3 py-2 font-medium text-primary-ink ' +
  'hover:opacity-90 active:opacity-80 disabled:opacity-60 disabled:hover:opacity-60';
export const ghostButtonClass =
  'w-full rounded-[var(--radius-control)] border border-border px-3 py-2 text-ink ' +
  'hover:bg-bg active:bg-border/40';

// Inline — side-by-side footer actions (FormSheet).
export const primaryButtonInlineClass =
  'rounded-[var(--radius-control)] bg-primary px-4 py-2 font-medium text-primary-ink ' +
  'hover:opacity-90 active:opacity-80 disabled:opacity-60 disabled:hover:opacity-60';
export const ghostButtonInlineClass =
  'rounded-[var(--radius-control)] border border-border px-4 py-2 text-ink ' +
  'hover:bg-bg active:bg-border/40';

export const checkboxClass =
  'h-4 w-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/30';

/**
 * §18.5 AmountField: accepts inline arithmetic (EX-4) and locale shorthand ("2 lakh",
 * "1.5k", AI-6). `value` is the raw text the user is typing; the caller parses again at
 * submit time via `parseAmountInput` to get the final minor-units amount.
 */
export function AmountField({
  id,
  currency,
  value,
  onChange,
  autoFocus,
  ariaLabel,
}: {
  id: string;
  currency: CurrencyCode;
  value: string;
  onChange: (raw: string) => void;
  autoFocus?: boolean;
  /** Set when this field has no adjacent <Field label=…> (e.g. a repeated split-line row). */
  ariaLabel?: string;
}): React.JSX.Element {
  const result =
    value.trim() === '' ? null : parseAmountInput(value, { defaultCurrency: currency });
  return (
    <div>
      <input
        id={id}
        className={inputClass}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        inputMode="decimal"
        placeholder={`e.g. 2 lakh, 1500, ${currency} 50`}
        autoFocus={autoFocus === true}
        {...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {})}
      />
      {result !== null &&
        (result.ok ? (
          <p className="mt-1 text-sm text-muted">{result.preview}</p>
        ) : (
          <p className="mt-1 text-sm text-danger">{result.error}</p>
        ))}
    </div>
  );
}
